import { useCompany } from './FetchConfig';
import { useContext } from 'react';
import CurrencyContext from '../components/Context/CurrencyContext';
import { ICurrency, ICurrencyInput, Maybe } from '../graphql/schema/p4types';

/**
 * @param currencyIso - 3 Character ISO Code
 */
export const getSymbolFromIso = (currencyIso: string) => {
  const symbol = new Intl.NumberFormat('en', { style: 'currency', currency: currencyIso })
    .formatToParts(0)
    .find((x) => x.type === 'currency');
  return symbol && symbol.value;
};

export const getSymbolFromCurrency = (currency: Maybe<ICurrency | ICurrencyInput>) => {
  if (currency?.isoCode?.codeAlpha3 && currency?.isoCode?.codeAlpha3 !== 'GBP') {
    return getSymbolFromIso(currency.isoCode.codeAlpha3);
  }
  return currency ? '£' : undefined;
};

export const useSymbolFromCurrency = (type: 'currency' | 'baseCurrency' | undefined) => {
  const company = useCompany();
  const { currency } = useContext(CurrencyContext);

  //Need to check type is a currency - Otherwise we would return ? as the symbol for non-currency fields.
  if (type !== 'currency' && type !== 'baseCurrency') return undefined;

  return getSymbolFromCurrency(type === 'baseCurrency' ? company?.baseCurrency : currency) || '£';
};

export const isValidCurrencyCode = (code: string): boolean => {
  const currencyCodeRegex = /^[A-Z]{3}$/;
  return currencyCodeRegex.test(code);
};

/**
 * Format the provided value with the appropriate currency symbol and negation symbol, as appropriate.
 * @param currency - 3 Digit currency code (I.e. 'GBP', 'EUR', etc).
 * @param decimals - Number of decimal places,
 * @param value - The value being formatted.
 */
export const formatCurrencyValue = (currency: string, decimals: number, value: number) => {
  //This is possibly slightly over-the-top since you can't have a negative 'value' here, but little harm in covering all cases.
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: decimals,
  }).format(value);
};

export const useBaseCurrencyAlpha3 = () => {
  const company = useCompany();
  return company?.baseCurrency?.isoCode?.codeAlpha3 ?? 'GBP';
};
